import axios, { AxiosResponse, CancelTokenSource } from 'axios';
import { getAuthInstance } from "../auth/authService";
import { ISignageItem, ISignageItemUpdate } from "../store/interfaces/ISignage";
import { v4 as uuidv4 } from 'uuid';

// Needed for a cancel token on requests
let source = null as CancelTokenSource | null;

// FLOCK-6612: unsaved items have no id until the first save, and minting a
// fresh uuid per preview request made every render look like a different
// signage item to the server, which bypassed the server-side preview
// supersede and render cache. Edits on a brand-new template therefore queued
// behind every abandoned render. One sticky id per unsaved draft keeps the
// whole editing session on a single identity; it resets whenever an item
// with a real id comes through.
let draftPreviewId = null as string | null;

export interface ISignagePreview {
    preview: string;
    thumb: string;
}

export interface ISignagePreviewService {
    updatePreview(signageItem: ISignageItem): Promise<ISignagePreview>;
    generatePreview(signageItemUpdate: ISignageItemUpdate): Promise<ISignagePreview | null>;
    generatePreviewRepo(signageUpdate: ISignageItemUpdate): Promise<ISignagePreview | null>;
}

export class SignagePreviewService implements ISignagePreviewService {
    async updatePreview(signageItem: ISignageItem): Promise<ISignagePreview> {

        const signageItemUpdate: ISignageItemUpdate = {
            signageItem: signageItem,
            generatePreview: true,
            previewRoute: signageItem.layoutType
        };

        const previewPromise = this.generatePreview(signageItemUpdate);

        return previewPromise.then((preview: any) => {
            if(preview) {
                return preview;
            }
        }).catch((error: any) => {
            throw error;
        })
    }

    async generatePreview(signageItemUpdate: ISignageItemUpdate): Promise<ISignagePreview | null> {
        if(signageItemUpdate.signageItem.refreshedLayout) return null;   
        
        // YouTube videos don't need preview images - they use iframes
        if(signageItemUpdate.signageItem.layoutType === 'youtubevideo') return null;
        
        if(!signageItemUpdate.generatePreview || !signageItemUpdate.previewRoute) return null;
        
        return await this.generatePreviewRepo(signageItemUpdate);   
    }

    async generatePreviewRepo(signageUpdate: ISignageItemUpdate): Promise<ISignagePreview | null> {
        // Check if we have a source applied to the request
        if (source) source.cancel('Operation canceled by the user.');
        // Assign the source factory to the source variable
        source = axios.CancelToken.source();
        
        let previewId = signageUpdate.signageItem.id;
        if (previewId) {
            draftPreviewId = null;
        } else {
            draftPreviewId = draftPreviewId || uuidv4();
            previewId = draftPreviewId;
        }
        const generatePreviewUrl = `${ process.env.VUE_APP_API_HOST }/api/signages/${previewId}/previewtemplate`;
        
        // Map old layout types to new ones for backward compatibility
        let previewRoute = signageUpdate.previewRoute;
        if (previewRoute === 'covid-occupancy') {
            previewRoute = 'occupancy-health';
        } else if (previewRoute === 'covidoccupancytwo') {
            previewRoute = 'occupancy-general';
        } else if (previewRoute === 'covid-safety') {
            previewRoute = 'health-tips';
        }
        
        const previewTemplateUrl = `${window.location.protocol}//${window.location.hostname}/signage/template/${previewRoute}/`;
        const authHeader = await getAuthInstance().getAxiosHeader();

        const options = {...authHeader, cancelToken: source.token }
        const payload = {
            data: signageUpdate.signageItem.layoutData,
            url: previewTemplateUrl
        }

        const axiosResponse = await axios.post(generatePreviewUrl, payload, {
            ...options,
            // FLOCK-6612: previews render server-side and can queue behind
            // other renders. Without a timeout a slow or lost request kept
            // isLoadingUpdatedPreview stuck (which also blocks Save). The
            // server's own response deadline is 300s; 120s bounds the wait
            // well inside it.
            timeout: 120000
        })
        .catch((thrown) => {
            if(axios.isCancel(thrown)) {
                // FLOCK-6612: a newer preview superseded this request; the
                // in-flight one drives the UI, so resolve null instead of
                // throwing (callers treat null as "canceled", per below).
                return null;
            }
            throw thrown;
        }) as AxiosResponse | null;

        // No axios response means the preview was canceled
        // and another preview was started.
        return axiosResponse ? axiosResponse.data : null;
    }
}

export default new SignagePreviewService;

